import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  type MutationFunction,
  type MutationKey,
} from '@tanstack/react-query';

type MutationOptionsWithoutKeyFn<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'mutationKey' | 'mutationFn'
>;

export function createMutation<TData, TError = unknown, TVariables = void, TContext = unknown>(
  base: {
    mutationKey?: MutationKey;
    mutationFn: MutationFunction<TData, TVariables>;
  } & MutationOptionsWithoutKeyFn<TData, TError, TVariables, TContext>,
): (
  options?: MutationOptionsWithoutKeyFn<TData, TError, TVariables, TContext>,
) => UseMutationResult<TData, TError, TVariables, TContext>;

export function createMutation<
  TParams extends any[],
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  builder: (...params: [...TParams]) => {
    mutationKey?: MutationKey;
    mutationFn: MutationFunction<TData, TVariables>;
  } & MutationOptionsWithoutKeyFn<TData, TError, TVariables, TContext>,
): (
  args: Readonly<[...TParams]>,
  options?: MutationOptionsWithoutKeyFn<TData, TError, TVariables, TContext>,
) => UseMutationResult<TData, TError, TVariables, TContext>;

export function createMutation(arg: any) {
  if (typeof arg !== 'function') {
    const base = arg as {
      mutationKey?: MutationKey;
      mutationFn: MutationFunction<any, any>;
    } & Record<string, unknown>;

    return function useFixedMutation(
      userOptions?: Record<string, unknown>,
    ): UseMutationResult<any, any, any, any> {
      return useMutation({
        ...(base as object),
        ...(userOptions as object),
        mutationKey: base.mutationKey,
        mutationFn: base.mutationFn,
      } as any);
    };
  }

  const builder = arg as (...params: any[]) => {
    mutationKey?: MutationKey;
    mutationFn: MutationFunction<any, any>;
  } & Record<string, unknown>;

  return function useBuiltMutation(
    args: readonly unknown[],
    userOptions?: Record<string, unknown>,
  ): UseMutationResult<any, any, any, any> {
    if (!Array.isArray(args)) {
      throw new Error('At least one parameter is required');
    }
    const cfg = builder(...args);
    const { mutationKey, mutationFn, ...builtOptions } = cfg;

    return useMutation({
      ...(builtOptions as object),
      ...(userOptions as object),
      mutationKey,
      mutationFn,
    } as any);
  };
}
